import { useState, useEffect, useRef } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { getPlans, createPayment } from '../api/payments';
import { useAuth } from '../store/authStore.jsx';
import { v4 as uuidv4 } from 'uuid';

const APP_ID     = import.meta.env.VITE_SQUARE_APPLICATION_ID || '';
const LOCATION_ID = import.meta.env.VITE_SQUARE_LOCATION_ID  || '';
const SQ_ENV     = import.meta.env.VITE_SQUARE_ENVIRONMENT   || 'sandbox';
const SQ_SDK_URL = SQ_ENV === 'production'
  ? 'https://web.squarecdn.com/v1/square.js'
  : 'https://sandbox.web.squarecdn.com/v1/square.js';

const PLAN_ICONS = { Free: '🆓', Pro: '⚡', Team: '👥', Enterprise: '🏢' };

export default function UpgradePage() {
  const { user, reload } = useAuth();
  const navigate          = useNavigate();
  const [selected, setSelected] = useState(null);
  const [sqLoaded, setSqLoaded] = useState(false);
  const [sqCard,   setSqCard]   = useState(null);
  const [error,    setError]    = useState('');
  const [success,  setSuccess]  = useState('');
  const cardContainerRef = useRef(null);

  const { data: plans = [] } = useQuery({
    queryKey: ['plans'],
    queryFn:  () => getPlans().then((r) => r.data),
  });

  const mutation = useMutation({
    mutationFn: createPayment,
    onSuccess: async (res) => {
      setSuccess(`You're now on the ${res.data.plan.name} plan!`);
      await reload();
      setTimeout(() => navigate('/dashboard'), 2000);
    },
    onError: (err) => setError(err.response?.data?.error || 'Payment failed.'),
  });

  // Load Square SDK script once
  useEffect(() => {
    if (!APP_ID || document.getElementById('square-sdk')) { setSqLoaded(true); return; }
    const s   = document.createElement('script');
    s.id      = 'square-sdk';
    s.src     = SQ_SDK_URL;
    s.onload  = () => setSqLoaded(true);
    s.onerror = () => setError('Failed to load payment SDK.');
    document.head.appendChild(s);
  }, []);

  // Init card when a paid plan is selected
  useEffect(() => {
    if (!sqLoaded || !selected || selected.price_label === '$0') return;
    if (!APP_ID) return; // no Square creds configured

    let card;
    async function initCard() {
      if (!window.Square) return;
      const payments = window.Square.payments(APP_ID, LOCATION_ID);
      card = await payments.card();
      await card.attach(cardContainerRef.current);
      setSqCard(card);
    }
    initCard().catch(() => setError('Could not initialise payment form.'));

    return () => { card?.destroy?.(); setSqCard(null); };
  }, [sqLoaded, selected]);

  async function handlePay() {
    setError('');
    if (selected.price_label === '$0') {
      mutation.mutate({ plan_id: selected.id, source_id: 'FREE', idempotency_key: uuidv4() });
      return;
    }
    if (!sqCard) return setError('Payment form not ready.');
    const result = await sqCard.tokenize();
    if (result.status !== 'OK') {
      return setError(result.errors?.[0]?.message || 'Card tokenisation failed.');
    }
    mutation.mutate({ plan_id: selected.id, source_id: result.token, idempotency_key: uuidv4() });
  }

  const currentPlanId = user?.plan_id;

  return (
    <div className="max-w-3xl mx-auto space-y-8">
      <div>
        <h1 className="text-2xl font-bold text-white">Upgrade your plan</h1>
        <p className="text-gray-400 text-sm mt-1">Choose the plan that fits your needs.</p>
      </div>

      {/* Plan cards */}
      <div className="grid sm:grid-cols-2 gap-4">
        {plans.map((plan) => {
          const isCurrent  = plan.id === currentPlanId;
          const isSelected = selected?.id === plan.id;
          return (
            <button
              key={plan.id}
              onClick={() => { setSelected(plan); setError(''); }}
              className={`card text-left p-5 transition-all ${
                isSelected
                  ? 'border-brand-500 ring-2 ring-brand-500/40'
                  : 'hover:border-gray-600'
              } ${isCurrent ? 'opacity-60' : ''}`}
              disabled={isCurrent}
            >
              <div className="flex items-start justify-between gap-2">
                <div>
                  <p className="text-lg font-bold text-white">
                    {PLAN_ICONS[plan.name] || ''} {plan.name}
                  </p>
                  <p className="text-brand-400 font-semibold mt-0.5">{plan.price_label}</p>
                </div>
                {isCurrent && (
                  <span className="text-xs bg-gray-700 text-gray-300 px-2 py-0.5 rounded-full">Current</span>
                )}
              </div>
              <ul className="mt-3 space-y-1 text-sm text-gray-400">
                <PlanItem label="Storage"        value={plan.storage_limit_mb === -1 ? 'Unlimited' : `${plan.storage_limit_mb >= 1024 ? `${plan.storage_limit_mb / 1024} GB` : `${plan.storage_limit_mb} MB`}`} />
                <PlanItem label="Monthly uploads" value={plan.monthly_upload_limit === -1 ? 'Unlimited' : plan.monthly_upload_limit} />
                <PlanItem label="Max file size"  value={`${plan.max_file_size_mb} MB`} />
                <PlanItem label="Folders"        value={plan.max_folders === -1 ? 'Unlimited' : plan.max_folders} />
                <PlanItem label="Categories"     value={plan.max_categories === -1 ? 'Unlimited' : plan.max_categories} />
              </ul>
            </button>
          );
        })}
      </div>

      {/* Payment form */}
      {selected && !success && (
        <div className="card p-6 space-y-4">
          <h2 className="font-semibold text-white">
            {selected.price_label === '$0' ? `Switch to ${selected.name}` : `Pay ${selected.price_label} for ${selected.name}`}
          </h2>

          {selected.price_label !== '$0' && APP_ID && (
            <div>
              <p className="text-xs text-gray-500 mb-2">Card details (secured by Square)</p>
              <div
                ref={cardContainerRef}
                className="border border-gray-700 rounded-lg p-3 bg-gray-800 min-h-[56px]"
              />
              {!sqCard && <p className="text-xs text-gray-500 mt-1 animate-pulse">Loading payment form…</p>}
            </div>
          )}

          {selected.price_label !== '$0' && !APP_ID && (
            <div className="bg-yellow-900/30 border border-yellow-700 text-yellow-300 rounded-lg p-3 text-sm">
              Square credentials not configured. Set <code>VITE_SQUARE_APPLICATION_ID</code> and
              <code> VITE_SQUARE_LOCATION_ID</code> in your <code>.env</code>.
            </div>
          )}

          {error   && <p className="text-red-400 text-sm">{error}</p>}
          {success && <p className="text-green-400 text-sm">{success}</p>}

          <div className="flex gap-3">
            <button
              className="btn-primary"
              disabled={mutation.isPending || (selected.price_label !== '$0' && !sqCard && !!APP_ID)}
              onClick={handlePay}
            >
              {mutation.isPending ? 'Processing…' : selected.price_label === '$0' ? 'Switch plan' : `Pay ${selected.price_label}`}
            </button>
            <button className="btn-secondary" onClick={() => { setSelected(null); setSqCard(null); }}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {success && (
        <div className="card p-6 text-center space-y-2">
          <p className="text-2xl">🎉</p>
          <p className="text-green-400 font-semibold">{success}</p>
          <p className="text-gray-500 text-sm">Redirecting to dashboard…</p>
        </div>
      )}
    </div>
  );
}

function PlanItem({ label, value }) {
  return (
    <li className="flex justify-between">
      <span>{label}</span>
      <span className="text-gray-200 font-medium">{value}</span>
    </li>
  );
}
